import React, { useState } from 'react';
import { Camera, Check, Loader2, Paperclip } from 'lucide-react';

// Dois gatilhos de anexo — "Tirar foto" e "Escolher arquivo" — cada um com o seu
// <input type="file"> escondido. Usado por todo campo do portal que aceita FOTO
// OU PDF (cupom do Petty Cash, comprovante do Estorno, documento das Demandas de
// RH, prova do Destaque do Mês, NF da Conservação).
//
// ⚠️⚠️ POR QUE DOIS INPUTS, E NÃO UM: o menu que o celular mostra ao tocar num
// anexo é montado pelo SISTEMA a partir do atributo `accept`.
//   · iOS/Safari sempre abre a mesma folha (Fototeca · Tirar Foto · Escolher
//     Arquivo), praticamente ignorando o `accept` — a câmera aparece sempre.
//   · Android/Chrome LÊ o `accept` e só acrescenta o app de Câmera quando ele é
//     puramente de mídia (`image/*`). Num `accept` que mistura imagem com PDF
//     — ou vazio — ele cai no seletor genérico de Documentos e a CÂMERA SOME.
// Era exatamente esse o sintoma relatado pelas lojas: no iPhone dava para
// fotografar o cupom, no Android o seletor ia direto para os arquivos. Não
// existe `accept` que atenda os dois casos num input só; a saída é separar.
//
// O componente ESCOLHE o arquivo e devolve pelo `onArquivo` — quem sobe (ou
// guarda no estado do formulário) é a tela que o usa. De propósito: das cinco
// telas, três seguram o File até o submit e duas sobem na hora.

// "Tirar foto" só faz sentido em celular/tablet: no desktop o `capture` é
// ignorado e o botão viraria um segundo "escolher arquivo", confundindo à toa.
// `pointer: coarse` = dedo em vez de mouse. Lido uma vez — na prática o
// dispositivo não troca de tipo de ponteiro no meio do preenchimento.
const usaDedo = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

const Gatilho = ({ Icone, texto, accept, capture, cor, preenchido, compacto, desabilitado, multiplo, onArquivo, onArquivos }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: compacto ? '5px 10px' : '9px 14px',
    borderRadius: compacto ? 6 : 8,
    fontSize: compacto ? 12 : 13.5,
    fontWeight: 700,
    maxWidth: '100%',
    border: '1px solid',
    borderColor: preenchido ? cor : '#cbd5e1',
    background: preenchido ? `${cor}14` : '#fff',
    color: preenchido ? cor : '#475569',
    cursor: desabilitado ? 'not-allowed' : 'pointer',
    opacity: desabilitado ? 0.6 : 1,
  }}>
    <Icone size={compacto ? 13 : 15} />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{texto}</span>
    <input
      type="file"
      accept={accept}
      capture={capture}
      multiple={multiplo}
      disabled={desabilitado}
      style={{ display: 'none' }}
      onChange={(e) => {
        const arquivos = Array.from(e.target.files || []);
        e.target.value = ''; // permite reescolher o MESMO arquivo depois de um erro
        if (!arquivos.length) return;
        // ⚠️ Multiplo entrega o LOTE de uma vez (`onArquivos`) para quem chama
        //    poder aplicar o teto olhando todos juntos e avisar o que sobrou. Sem
        //    `onArquivos`, cai no `onArquivo` por arquivo — o multiplo funciona
        //    mesmo sem o callback de lote. Single (o caso dos 5 outros usos)
        //    segue exatamente como antes: só o primeiro.
        if (multiplo && onArquivos) onArquivos(arquivos);
        else if (multiplo) arquivos.forEach((f) => onArquivo(f));
        else onArquivo(arquivos[0]);
      }}
    />
  </label>
);

/**
 * @param {string}   accept       tipos aceitos no botão "Escolher arquivo" (o da foto é sempre `image/*`)
 * @param {function} onArquivo    recebe o File escolhido — a tela decide o que fazer com ele
 * @param {string}   cor          cor do módulo (ver ARQUITETURA.md › Paleta dos módulos)
 * @param {string}   nome         nome do arquivo já escolhido; vira o rótulo do botão e o pinta de "pronto"
 * @param {boolean}  ocupado      true enquanto a tela sobe/lê o arquivo
 * @param {boolean}  desabilitado trava os dois gatilhos
 * @param {boolean}  compacto     versão miúda, para dentro de célula de tabela / card estreito
 * @param {string}   rotulo       texto do botão de arquivo quando nada foi escolhido
 * @param {boolean}  multiplo     permite escolher VÁRIOS arquivos no gatilho de galeria
 *                                (a câmera é sempre 1 foto). Default false — os usos de
 *                                "foto OU PDF" seguem single, sem mudar nada.
 * @param {function} onArquivos   recebe o LOTE (File[]) quando `multiplo` — quem chama
 *                                aplica o teto olhando todos juntos e avisa o que sobrou.
 *                                Sem ele, o multiplo cai no `onArquivo` por arquivo.
 */
const EscolherAnexo = ({
  accept = 'image/*,application/pdf',
  onArquivo,
  cor = '#475569',
  nome = '',
  ocupado = false,
  desabilitado = false,
  compacto = false,
  rotulo = 'Escolher arquivo',
  multiplo = false,
  onArquivos,
}) => {
  const [mobile] = useState(usaDedo);
  const travado = desabilitado || ocupado;

  if (ocupado) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: compacto ? 12 : 13.5, fontWeight: 700, color: cor,
      }}>
        <Loader2 size={compacto ? 13 : 15} className="spin" /> Enviando…
      </span>
    );
  }

  return (
    <div style={{
      display: 'flex', gap: compacto ? 5 : 8, flexWrap: 'wrap',
      // Compacto empilha (cabe em célula de tabela); normal fica lado a lado.
      flexDirection: compacto ? 'column' : 'row',
      alignItems: compacto ? 'stretch' : 'center',
    }}>
      {mobile && (
        <Gatilho
          Icone={Camera} texto="Tirar foto"
          accept="image/*" capture="environment"
          cor={cor} preenchido={false} compacto={compacto}
          desabilitado={travado} onArquivo={onArquivo}
        />
      )}
      <Gatilho
        Icone={nome ? Check : Paperclip} texto={nome || rotulo}
        accept={accept}
        cor={cor} preenchido={Boolean(nome)} compacto={compacto}
        desabilitado={travado} multiplo={multiplo}
        onArquivo={onArquivo} onArquivos={onArquivos}
      />
    </div>
  );
};

export default EscolherAnexo;
